import React from 'react';
import styled from 'styled-components';
import { Heading } from '../components/Heading';
import { motion } from 'framer-motion';
import { theme } from '../styles/GlobalStyles';

const ServicesHero = styled.section`
  position: relative;
  width: 100vw;
  min-height: 70vh;
  padding-top: 100px; /* Account for standard navbar */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ServicesHeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%);
  z-index: 1;
`;

const ServicesHeroBg = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background: url('/img_to_use/pexels-olly-840996.jpg') center/cover no-repeat;
  transform: scale(1);
  animation: zoomServicesHero 18s ease-in-out infinite alternate;
  will-change: transform;
  transition: transform 0.7s cubic-bezier(0.4,0,0.2,1);

  @keyframes zoomServicesHero {
    0% { transform: scale(1);}
    100% { transform: scale(1.08);}
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      animation-play-state: paused;
      transform: scale(1.16);
      transition: transform 0.7s cubic-bezier(0.4,0,0.2,1);
    }
  }
`;

const ServicesHeroContent = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  z-index: 2;
  padding: 0 2rem;
`;

const ServicesHeroTitle = styled.h1`
  font-size: 4.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.6), 0 0 12px ${theme.colors.accent}33;
  color: ${theme.colors.accent};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2.8rem;
  }
`;

const ServicesHeroSubtitle = styled.p`
  font-size: 1.6rem;
  margin-bottom: 2.5rem;
  font-weight: 300;
  text-shadow: 1px 1px 6px rgba(0, 0, 0, 0.8);
  color: #FFFFFF;
  opacity: 0.95;
`;

const ServicesSection = styled.section`
  background: ${theme.colors.background};
  width: 100%;
  padding: clamp(4rem, 8vw, 8rem) 0;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: clamp(1.5rem, 3vw, 2.5rem);
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.08);
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  display: flex;
  flex-direction: column;
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 28px rgba(0,0,0,0.12);
  }
  img {
    width: 100%;
    height: 180px;
    object-fit: cover;
  }
  h3 {
    color: ${theme.colors.primary};
    font-size: 1.2rem;
    font-weight: 700;
    margin: 1rem 1rem 0.5rem 1rem;
    font-family: 'Playfair Display', serif;
  }
  p {
    color: ${theme.colors.text};
    font-size: 0.98rem;
    line-height: 1.6;
    margin: 0 1rem 1.25rem 1rem;
    font-family: 'Inter', sans-serif;
  }
`;

const services = [
  {
    title: 'Executive Leadership Hiring',
    img: '/img_to_use/leadership.jpg',
    desc: 'De Arc delivers top-tier executive and leadership talent for your most critical roles. Our proven search process ensures you get visionary leaders who drive success.'
  },
  {
    title: 'Recruitment Solutions',
    img: '/img_to_use/recruitment solution.jpg',
    desc: 'De Arc matches the right talent to your business needs, providing seamless recruitment solutions that enhance your team and accelerate your growth.'
  },
  {
    title: 'Project-Based Recruitment',
    img: '/img_to_use/project based recruitment.jpg',
    desc: 'For bulk or turnkey hiring, De Arc offers agile, scalable project-based recruitment to help you ramp up quickly and efficiently.'
  },
  {
    title: 'Recruitment Process Outsourcing',
    img: '/img_to_use/outsourcing blueprint.jpg',
    desc: 'Let De Arc manage your end-to-end recruitment process, so you can focus on your core business. Flexible, transparent, and results-driven.'
  }
];

const ServicesPage: React.FC = () => {
  return (
    <>
      <ServicesHero>
        <ServicesHeroBg />
        <ServicesHeroOverlay />
        <ServicesHeroContent>
          <ServicesHeroTitle>Our Services</ServicesHeroTitle>
          <ServicesHeroSubtitle>
            Comprehensive staffing solutions tailored to your business needs
          </ServicesHeroSubtitle>
        </ServicesHeroContent>
      </ServicesHero>

      <ServicesSection>
        <Heading level="h2" align="center" style={{ fontFamily: theme.fonts.secondary, color: theme.colors.text, fontWeight: theme.typography.h2.fontWeight, marginBottom: '2.5rem' }}>
          Explore Our Expertise
        </Heading>
        <ServicesGrid>
          {services.map((service, idx) => (
            <ServiceCard
              key={service.title}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: idx * 0.1 }}
              style={{ cursor: 'default' }}
            >
              <img src={service.img} alt={service.title} />
              <h3>{service.title}</h3>
              <p>{service.desc}</p>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </ServicesSection>
    </>
  );
};

export default ServicesPage; 